import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import { AppearanceService } from '@services/appearance.service';
import { LocalStorageService } from '@services/local-storage.service';

// --- Stores ---
import { ColorSchemesStore } from '@stores/color-schemes.store';
import { ThemesStore } from '@stores/themes.store';

// --- Utils ---
import { setThemeConfig, toRGBTheme } from '@utils/root-css-variables.util';

// --- Interfaces ---
import type { IColorScheme, ITheme } from '@interfaces/appearance';
import type { AttachFileData } from '@interfaces/file';

const REFRESH_INTERVAL = 120 * 1000; // 1 min 30sec

@Injectable({
	providedIn: 'root',
})
export class AppearanceFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);

	companyName = '';
	loadedTheme = false;
	logo: string = '';
	showMenu = false;
	useTheme = false;

	private readonly handleRequestTheme$ = combineLatest([
		this.autoRefresh$,
		this._refresh.asObservable(),
	]).pipe(switchMap(() => this.getThemes()));

	readonly themeState$ = combineLatest([
		this.themesStore.themesState$,
		this.handleRequestTheme$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	private readonly handleRequestColorScheme$ = combineLatest([
		this._refresh.asObservable(),
	]).pipe(switchMap(() => this.getColorSchemes()));

	readonly colorSchemeState$ = combineLatest([
		this.colorSchemesStore.colorSchemesState$,
		this.handleRequestColorScheme$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private appearanceService: AppearanceService,
		private colorSchemesStore: ColorSchemesStore,
		private localStorageService: LocalStorageService,
		private themesStore: ThemesStore
	) {}

	// Color Schemes
	getColorSchemes() {
		return this.appearanceService
			.getColorSchemes()
			.pipe(
				tap(colorSchemes =>
					this.colorSchemesStore.updateColorSchemes(colorSchemes)
				)
			);
	}

	getThemeSearch(hostname: string) {
		return this.appearanceService.getThemeSearch(hostname);
	}

	newColorSchemes(colorScheme: IColorScheme) {
		return this.appearanceService
			.postColorScheme(colorScheme)
			.pipe(
				tap(colorScheme =>
					this.colorSchemesStore.updateColorScheme(colorScheme)
				)
			);
	}

	updateColorScheme(colorScheme: IColorScheme) {
		return this.appearanceService
			.putColorScheme(colorScheme.id, colorScheme)
			.pipe(
				tap(colorScheme =>
					this.colorSchemesStore.updateColorScheme(colorScheme)
				)
			);
	}

	// Themes
	getThemes() {
		return this.appearanceService.getThemes().pipe(
			shareReplay(),
			tap(themes => this.themesStore.updateThemes(themes))
		);
	}

	getThemeById(id: ITheme['id']) {
		return this.appearanceService.getTheme(id);
	}

	changeTheme(id: any) {
		return this.appearanceService.changeTheme(id).pipe(
			shareReplay(),
			tap(theme => this.themesStore.updateTheme(theme))
		);
	}

	newTheme(theme: ITheme) {
		return this.appearanceService.postTheme(theme).pipe(
			shareReplay(),
			tap(theme => this.themesStore.updateTheme(theme))
		);
	}

	updateTheme(theme: ITheme) {
		return this.appearanceService.putTheme(theme.id, theme).pipe(
			shareReplay(),
			tap(theme => this.themesStore.updateTheme(theme))
		);
	}

	deleteTheme(id: ITheme['id']) {
		return this.appearanceService
			.deleteTheme(id)
			.pipe(tap(() => this.themesStore.deleteTheme(id)));
	}

	//Logo
	saveLogo(image: AttachFileData, theme: ITheme) {
		return this.appearanceService
			.postLogo(image, theme.id)
			.pipe(tap(theme => this.themesStore.updateTheme(theme)));
	}

	//Theme - LocalStorage
	setStorageTheme(theme: ITheme) {
		this.localStorageService.set('theme', theme);
		this.applyTheme(theme);
	}

	getStorageTheme() {
		const theme = this.localStorageService.get<ITheme>('theme');
		if (theme) this.applyTheme(theme);

		return theme;
	}

	applyTheme(theme: ITheme) {
		if (!theme) return;

		setThemeConfig(toRGBTheme(theme.colorScheme));
		this.useTheme = true;
		this.getLogo();
	}

	getLogo() {
		const themeData = this.localStorageService.get<ITheme>('theme');
		if (themeData?.companyName) this.logo = themeData.companyName;
		if (themeData?.companyLogoUrl) this.logo = themeData.companyLogoUrl;
	}

	removeTheme() {
		this.useTheme = false;

		const rootElement =
			document.documentElement ||
			document.body.parentElement ||
			document.querySelector('html');
		rootElement.removeAttribute('style');
	}
}
